import type { Product } from '@app/src/types/product'
import type {
  HistoryStatus,
  QuestionRule,
  ReportAccessModel,
  Request,
  RequestHistory,
  ResponseDraftStatus,
  ResponseWithPreviousCorrectionNeededItem,
} from '@app/src/types/resourceExplorer'
import { createContext, useContext } from 'react'
import { ReuseAllQuestionsStates } from './ReportContextProvider'

export interface LanguageInfo {
  code: string
  name: string
}

export type ReportContextProps = {
  responseId?: number
  requestId?: number
  request?: Request
  response?: ResponseWithPreviousCorrectionNeededItem
  requestsPerPeriod?: Request[]
  currentAccess?: ReportAccessModel
  requestHistory?: RequestHistory[]
  latestHistory?: RequestHistory
  reportStatus?: ResponseDraftStatus | HistoryStatus
  targetProduct?: Product
  isPreview?: boolean
  locale: LanguageInfo | null
  showAccessNudge: boolean
  isRequestLoading: boolean
  isResponseLoading: boolean
  isResponseFetching: boolean
  isCurrentAccessLoading: boolean
  isRequestHistoryLoading: boolean
  isRequestsPerPeriodLoading: boolean
  isLoadingAccessNudge: boolean
  savedResponseHasChangesFromLastSubmitted: boolean
  reuseAllQuestionsState: ReuseAllQuestionsStates
  updateResponseIdParam: (responseId?: string | number) => void
  updateRequestIdParam: (requestId?: string | number) => void
  setReportLocale: (locale: LanguageInfo | null) => void
  setShowAccessNudge: (show: boolean) => void
  setLoadingAccessNudge: (loading: boolean) => void
  setReuseAllQuestionsState: (state: ReuseAllQuestionsStates) => void
  showPreviousAnswerSuggestions: boolean
  setShowPreviousAnswerSuggestions: (state: boolean) => void
  openPostSubmitFlow: () => void
  markRepositoryCopy: (responseItemId: number, fileName: string) => void
  requestItemsRules?: QuestionRule[]
}

const ReportContext = createContext<ReportContextProps>({
  isPreview: false,
  locale: null,
  showAccessNudge: false,
  isRequestLoading: true,
  isResponseFetching: true,
  isResponseLoading: true,
  isCurrentAccessLoading: true,
  isRequestHistoryLoading: true,
  isRequestsPerPeriodLoading: true,
  isLoadingAccessNudge: true,
  savedResponseHasChangesFromLastSubmitted: false,
  reuseAllQuestionsState: ReuseAllQuestionsStates.Review,
  updateResponseIdParam: () => undefined,
  updateRequestIdParam: () => undefined,
  setReportLocale: () => undefined,
  setShowAccessNudge: () => undefined,
  setLoadingAccessNudge: () => undefined,
  setReuseAllQuestionsState: () => undefined,
  showPreviousAnswerSuggestions: true,
  setShowPreviousAnswerSuggestions: () => undefined,
  openPostSubmitFlow: () => undefined,
  markRepositoryCopy: () => undefined,
  requestItemsRules: [],
})

export const useReport = (): ReportContextProps => {
  return useContext(ReportContext)
}

export default ReportContext
